(function (formatMoney) {
  formatMoney = formatMoney || ((price) => {
    return `$${price/100}`
  })

  /**
   * @abstract
   */
  class DynamicPricesCommon {
    /** @type {Product} */
    product = {};
    customerRoles = document.body.dataset.customerRole.split('|');
    customerDiscountedRole = '';
    rolesMap = new Map([
      ['concierge-bulk', 'msrp_minus_10_'],
      ['pro-deal', 'msrp_minus_30_'],
      ['expertvoice', 'msrp_minus_20_'],
      ['retail-rescue-30', 'msrp_minus_30_'],
      ['employee-feed', 'msrp_minus_30_'],
      ['direct-to-employee', 'direct_to_employee'],
      ['wholesale', 'wholesale_price']
    ]);

    /** @type {number | [number, number]} - array if range */
    price;

    /** @type {number | [number, number]} - array if range */
    discountPrice;

    /** @type {number} */
    discount = 0;

    /**
     * @param {Product} product
     * @returns {DynamicPricesResult}
     */
    constructor() {
      if (this.constructor === DynamicPricesCommon) {
        throw new Error('Can\'t instantiate abstract class!');
      }
    }

    /**
     * @param {Product} product
     * @returns {DynamicPricesResult}
     */
    getDynamicPrices = product => {
      if (!product) {
        throw new Error('No products provided')
      }

      this.product = product;
      this.customerDiscountedRole = this.getCustomerDiscountedRole();
      this.price = this.getPrice();

      if (this.customerDiscountedRole) {
        this.discountPrice = this.getDiscountPrice();
        this.discount = this.getDiscount();
      }

      return this.formatResult(
        this.price,
        this.discountPrice,
        this.discount
      );
    }

    getCustomerDiscountedRole() {
      for (const role of this.rolesMap.keys()) {
        if (this.customerRoles.includes(role)) {
          return role;
        }
      }

      return '';
    };

    /**
     * @param {number} price - e.g. 13.99
     * @returns {string} - Formatter price string, e.g. "$13.99"
     */
    format(price) {
      if (!price) {
        return '';
      }

      return formatMoney(price * 100);
    }

    formatDiscount(discount) {
      if (!discount) {
        return '';
      }

      return `${discount}% off`;
    }

    getPrice() {
      throw new Error('Method should be implemented');
    }

    getDiscountPrice() {
      throw new Error('Method should be implemented');
    }

    getDiscount() {
      throw new Error('Method should be implemented');
    }

    formatResult() {
      throw new Error('Method should be implemented');
    }
  }

  class DynamicPrices extends DynamicPricesCommon {
    constructor() {
      super();

      this.price = 0;
      this.discountPrice = 0;
    }
    getPrice() {
      return this.product.price;
    }

    getDiscountPrice() {
      const discountPriceFieldName = this.rolesMap.get(this.customerDiscountedRole);
      return this.product.meta.pricing[discountPriceFieldName];
    }

    getDiscount() {
      return Math.round((1 - this.discountPrice / this.price) * 100);
    }

    formatResult(price, discountPrice, discount) {
      return [
        this.format(price),
        this.format(discountPrice),
        this.formatDiscount(discount),
        this.customerDiscountedRole
      ];
    }
  }

  class DynamicPricesRange extends DynamicPricesCommon {
    constructor() {
      super();

      this.price = [0, 0];
      this.discountPrice = [0, 0];
      this.priceRangeFieldsMap = {
        'wholesale_minus_4': 'wholesale_minus4_variant_range',
        'wholesale_price': 'wholesale_variant_range',
        'direct_to_employee': 'directtoemployee_variant_range',
        'msrp_minus_10_': 'msrp_minus_10_variant_range',
        'msrp_minus_20_': 'msrp_minus_20_variant_range',
        'msrp_minus_40_': 'msrp_minus_40_variant_range',
        'msrp_minus_30_': 'msrp_minus_30_variant_range',
        'msrp_wholesale_case': 'msrp_wholesale_case',
        'msrp_minus_5_': 'msrp_minus_5_variant_range',
        'msrp_sale_price': 'msrp_sale_price_variant_range',
        'direct_to_customer': 'directtocustomer_variant_range',
        'price': 'msrp_variant_range',
      };
    }

    getPrice() {
      const priceFieldName = this.priceRangeFieldsMap.price;
      return this.unformatPriceRange(this.product.meta.pricing[priceFieldName]);
    }

    getDiscountPrice() {
      const discountPriceFieldName = this.rolesMap.get(this.customerDiscountedRole);
      const discountPriceRangeFieldName = this.priceRangeFieldsMap[discountPriceFieldName];
      return this.unformatPriceRange(
        this.product.meta.pricing[discountPriceRangeFieldName]
      );
    }

    getDiscount() {
      const priceMax = this.price[1];
      const discountPriceMax = this.discountPrice[1];
      return Math.round((1 - discountPriceMax / priceMax) * 100);
    }

    // because price is a formatted string from the API but to calculate discount numbers are needed
    unformatPriceRange(formatterPriceRange) {
      return formatterPriceRange
        .split(' - ')
        .map(priceStr => Number(priceStr.replace('$', '') || null));
    }

    formatRange([min, max]) {
      if (!min && !max) {
        return '';
      }
      
      // max === undefined if the price string isn't a range
      if (max === undefined) {
        return `${this.format(min)}`;
      }

      return `${this.format(min)} - ${this.format(max)}`;
    }

    formatResult(price, discountPrice, discount) {
      return [
        this.formatRange(price),
        this.formatRange(discountPrice),
        this.formatDiscount(discount),
        this.customerDiscountedRole
      ];
    }
  }


  try {
    /**
     * @param {Product} product
     * @param {Object} config
     * @param {boolean} config.noRange=false - Force using no range class
     * @returns {DynamicPricesResult}
     */
    window.getDynamicPrices = (
      product,
      config = {
        noRange: false
      }
    ) => {
      const { noRange } = config;
      const isRange = noRange ?
        false :
        product.variants_min_price !== product.variants_max_price;
      const instanceNoRange = new DynamicPrices();
      const instanceRange = new DynamicPricesRange();
      return isRange ? instanceRange.getDynamicPrices(product) : instanceNoRange.getDynamicPrices(product);
    };
  } catch (err) {
      console.error(err);
  }
})(window.Shopify.formatMoney?.bind(window.Shopify));


/**
 * @typedef {Object} Product - Described only field are needed to calculate price.
 *
 * @property {number} price - prices as a number, e.g. 13.99
 * @property {number} variants_max_price - prices as a number, e.g. 13.99
 * @property {number} variants_min_price - prices as a number, e.g. 13.99
 *
 * @property {number} meta.wholesale_minus_4 - prices as a number, e.g. 13.99
 * @property {number} meta.wholesale_price - prices as a number, e.g. 13.99
 * @property {number} meta.direct_to_employee - prices as a number, e.g. 13.99
 * @property {number} meta.msrp_minus_10_ - prices as a number, e.g. 13.99
 * @property {number} meta.msrp_minus_20_ - prices as a number, e.g. 13.99
 * @property {number} meta.msrp_minus_40_ - prices as a number, e.g. 13.99
 * @property {number} meta.msrp_minus_30_ - prices as a number, e.g. 13.99
 * @property {number} meta.msrp_wholesale_case - prices as a number, e.g. 13.99
 * @property {number} meta.msrp_minus_5_ - prices as a number, e.g. 13.99
 * @property {number} meta.msrp_sale_price - prices as a number, e.g. 13.99
 * @property {number} meta.direct_to_customer - prices as a number, e.g. 13.99
 * @property {number} meta.price - prices as a number, e.g. 13.99
 *
 * @property {string} meta.wholesale_minus4_variant_range - prices range as a string, e.g. "$10.44 - $15.19"
 * @property {string} meta.wholesale_variant_range - prices range as a string, e.g. "$10.44 - $15.19"
 * @property {string} meta.directtoemployee_variant_range - prices range as a string, e.g. "$10.44 - $15.19"
 * @property {string} meta.msrp_minus_10_variant_range - prices range as a string, e.g. "$10.44 - $15.19"
 * @property {string} meta.msrp_minus_20_variant_range - prices range as a string, e.g. "$10.44 - $15.19"
 * @property {string} meta.msrp_minus_40_variant_range - prices range as a string, e.g. "$10.44 - $15.19"
 * @property {string} meta.msrp_minus_30_variant_range - prices range as a string, e.g. "$10.44 - $15.19"
 * @property {string} meta.msrp_wholesale_case - prices range as a string, e.g. "$10.44 - $15.19"
 * @property {string} meta.msrp_minus_5_variant_range - prices range as a string, e.g. "$10.44 - $15.19"
 * @property {string} meta.msrp_sale_price_variant_range - prices range as a string, e.g. "$10.44 - $15.19"
 * @property {string} meta.directtocustomer_variant_range - prices range as a string, e.g. "$10.44 - $15.19"
 * @property {string} meta.msrp_variant_range - prices range as a string, e.g. "$10.44 - $15.19"
 */

/**
 * @typedef {string} MainPrice - Formatter price string, e.g. "$13.99" or "$10.44 - $15.19"
 * @typedef {string} OldPrice - Old/full/strikethrough formatter price string, e.g. "$13.99" or "$10.44 - $15.19"
 * @typedef {string} Discount - Discount, e.g. "-30%"
 * @typedef {[MainPrice, OldPrice, Discount]} DynamicPricesResult
 */
